import React, { CSSProperties, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorMessage, withFormik, FormikProps } from 'formik';
import { Form } from 'semantic-ui-react';
import styled from 'styled-components';

import { userSchema } from '@/services/validators/user';
import { User } from '@/types/userList';
import { FormValues } from '@/components/pages/Users/Index/UserListForm';

type OuterProps = {
  isUpdating: boolean;
  onCancelClick: (e: MouseEvent<HTMLAnchorElement>) => void;
  onSubmit: (values: FormValues) => Promise<void>;
  recomputeRowHeights: () => void;
  style: CSSProperties;
  user: User;
};

type Values = Pick<
  User,
  | 'lastName'
  | 'firstName'
  | 'kanaLastName'
  | 'kanaFirstName'
  | 'email'
  | 'nameId'
>;

export const UserFormContents = withFormik<OuterProps, Values>({
  mapPropsToValues: ({ user }) => ({
    lastName: user.lastName,
    firstName: user.firstName,
    kanaLastName: user.kanaLastName,
    kanaFirstName: user.kanaFirstName,
    email: user.email,
    nameId: user.nameId,
  }),
  validationSchema: userSchema,
  handleSubmit: async (values, { props }) => {
    await props.onSubmit(values);
    props.recomputeRowHeights();
  },
})(
  // enhanced form
  ({
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    values,
    onCancelClick,
    recomputeRowHeights,
    isUpdating,
  }: OuterProps & FormikProps<Values>) => {
    const { t } = useTranslation();

    return (
      <Form onSubmit={handleSubmit}>
        <Form.Group>
          <Form.Field required={true} width={3}>
            <Label>{t('models.user.lastName')}</Label>
            <input
              disabled={true}
              name="lastName"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.lastName}
            />
            <Span>
              <ErrorMessage name="lastName" />
            </Span>
          </Form.Field>
          <Form.Field required={true} width={3}>
            <Label>{t('models.user.firstName')}</Label>
            <input
              disabled={true}
              name="firstName"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.firstName}
            />
            <Span>
              <ErrorMessage name="firstName" />
            </Span>
          </Form.Field>
          <Form.Field required={true} width={3}>
            <Label>{t('models.user.kanaLastName')}</Label>
            <input
              disabled={true}
              name="kanaLastName"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.kanaLastName}
            />
            <Span>
              <ErrorMessage name="kanaLastName" />
            </Span>
          </Form.Field>
          <Form.Field required={true} width={3}>
            <Label>{t('models.user.kanaFirstName')}</Label>
            <input
              disabled={true}
              name="kanaFirstName"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.kanaFirstName}
            />
            <Span>
              <ErrorMessage name="kanaFirstName" />
            </Span>
          </Form.Field>
        </Form.Group>
        <Form.Group>
          <Form.Field required={true} width={5}>
            <Label>{t('models.user.email')}</Label>
            <input
              disabled={true}
              name="email"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.email}
            />
            <Span>
              <ErrorMessage name="email" />
            </Span>
          </Form.Field>
          <Form.Field width={3}>
            <Label>{t('models.user.nameId')}</Label>
            <input
              name="nameId"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.nameId || ''}
            />
            <Span>
              <ErrorMessage name="nameId" />
            </Span>
          </Form.Field>
        </Form.Group>
        <Action>
          <A
            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
              onCancelClick(e);
              recomputeRowHeights();
            }}
          >
            {t('ui.action.cancel')}
          </A>
          <Form.Button
            disabled={isSubmitting}
            loading={isUpdating}
            primary={true}
            type="submit"
          >
            {t('ui.action.updateNow')}
          </Form.Button>
        </Action>
      </Form>
    );
  },
);

const Action = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

const A = styled.a`
  cursor: pointer;
  font-size: 12px;
  margin-right: 24px;
`;

const Label = styled.label`
  &&&& {
    font-size: 12px !important;
  }
`;

const Span = styled.span`
  color: #9f3a38;
`;
